import Bar from "./Bar";

// Props to spread onto an antd <Table> during a first load.
//
// A column can declare what its cell actually looks like:
//   skeleton: "thumb"    77px square  (ListingThumbnail)
//   skeleton: "twoLine"  address block
//   skeleton: "actions"  row of icon buttons
//   skeleton: "text"     single line   (default)
// Sizing matters more than it looks: a thumbnail cell rendered as a text bar
// makes the placeholder row ~55px shorter than the real one, so the whole table
// jumps the moment data arrives.
//
// Everything that would make antd *read* or *reorder* the placeholder rows is
// stripped — `dataIndex` (no fields to read) plus the filter/sort props. A
// controlled `filteredValue` left in place would run `onFilter` against the
// fake rows, read undefined, and filter the whole skeleton away.
const passive = ({
  dataIndex,
  filters,
  onFilter,
  filteredValue,
  filterIcon,
  filterDropdown,
  sorter,
  sortOrder,
  defaultSortOrder,
  sortDirections,
  skeleton,
  ...col
}) => col;

// Ragged widths so a row of text cells reads as text, not as a uniform grid.
const TEXT_WIDTHS = ["78%", "52%", "64%", "45%", "70%", "58%"];

const SHAPES = {
  thumb: () => <Bar w={77} h={77} r={8} />,

  twoLine: () => (
    <div className="flex flex-col gap-1.5">
      <Bar w="82%" h={12} />
      <Bar w="58%" h={12} />
    </div>
  ),

  actions: () => (
    <div className="flex items-center gap-3.5">
      <Bar w={20} h={20} />
      <Bar w={20} h={20} />
      <Bar w={20} h={20} />
    </div>
  ),

  text: (i) => <Bar w={TEXT_WIDTHS[i % TEXT_WIDTHS.length]} h={12} />,
};

// `rowCount` should be the table's real page size. Showing 5 placeholders for a
// 10- or 20-row page is what makes the swap read as a flicker: the table grows
// by the difference the moment data lands. The footer bar reserves the pager's
// space for the same reason.
export const skeletonTableProps = (columns, rowCount = 10) => ({
  rowKey: "key",
  pagination: false,
  footer: () => (
    <div className="flex justify-center py-1">
      <Bar w={232} h={32} r={6} />
    </div>
  ),
  dataSource: Array.from({ length: rowCount }, (_, i) => ({
    key: `skeleton-${i}`,
  })),
  columns: columns.map((column, i) => {
    const shape = SHAPES[column.skeleton] ?? SHAPES.text;
    const col = passive(column);
    return {
      ...col,
      key: col.key ?? `skeleton-col-${i}`,
      render: () => shape(i),
    };
  }),
});
